import { useAuthStore } from '@/stores/authStore'
import { useBillingStatus } from '@/stores/subscriptionStore'
import type { Period, SubscriptionState } from '@/types/subscription'
import { getGracePeriodStatus, isInGracePeriod } from '@/utils/billingDateUtils'
import { periodToBillingCycle, resolveActiveOrganization, subscriptionStateToStatus } from '@/utils/subscriptionHelpers'
import { useMemo } from 'react'

export interface PlanExpirationStatus {
	isExpired: boolean
	isInGracePeriod: boolean
	gracePeriodDays: number
	status: 'active' | 'grace_full_access' | 'grace_restricted' | 'expired'
	nextBillingDate: string | null
	expirationDate: string | null
	isAccessBlocked: boolean
	isPendingInitialPayment?: boolean
}

export function usePlanExpiration(): PlanExpirationStatus {
	const billingStatus = useBillingStatus()
	const user = useAuthStore(s => s.user)
	const currentOrgId = useAuthStore(s => s.currentOrganizationId)
	const currentTenant = useAuthStore(s => s.currentTenant)

	return useMemo(() => {
		const resolvedOrg = user ? resolveActiveOrganization(user, currentOrgId, currentTenant) : null
		const isPendingInitialPayment =
			resolvedOrg?.subscription?.state === 'pending_initial_payment' ||
			billingStatus?.subscription_state === 'pending_initial_payment'

		if (isPendingInitialPayment) {
			return {
				isExpired: true,
				isInGracePeriod: false,
				gracePeriodDays: 0,
				status: 'expired',
				nextBillingDate: null,
				expirationDate: null,
				isAccessBlocked: false,
				isPendingInitialPayment: true,
			}
		}

		// 1. Check effective_subscription_state from current organization first
		if (user && resolvedOrg) {
			if (resolvedOrg.effective_subscription_state) {
				const state = resolvedOrg.effective_subscription_state
				if (state === 'about-to-be-cancelled') {
					return {
						isExpired: true,
						isInGracePeriod: true,
						gracePeriodDays: 0,
						status: 'grace_full_access',
						nextBillingDate: resolvedOrg.subscription?.expires_at || null,
						expirationDate: resolvedOrg.subscription?.expires_at || null,
						isAccessBlocked: false,
					}
				}
				if (state === 'cancelled') {
					return {
						isExpired: true,
						isInGracePeriod: false,
						gracePeriodDays: 0,
						status: 'expired',
						nextBillingDate: resolvedOrg.subscription?.expires_at || null,
						expirationDate: resolvedOrg.subscription?.expires_at || null,
						isAccessBlocked: true,
					}
				}
				if (state === 'active') {
					return {
						isExpired: false,
						isInGracePeriod: false,
						gracePeriodDays: 0,
						status: 'active',
						nextBillingDate: resolvedOrg.subscription?.expires_at || null,
						expirationDate: null,
						isAccessBlocked: false,
					}
				}
			}
		}

		let resolvedBilling = billingStatus

		// Fallback to active organization subscription for Platform/Cloud deployments
		if (!resolvedBilling && user) {
			const resolvedOrg = resolveActiveOrganization(user, currentOrgId, currentTenant)

			const subscription = resolvedOrg?.subscription

			if (subscription) {
				resolvedBilling = {
					email: user.email,
					current_plan: subscription.plan_name || '',
					billing_frequency: periodToBillingCycle((subscription.period || 'Monthly') as Period),
					subscription_status: subscriptionStateToStatus((subscription.state || 'active') as SubscriptionState),
					subscription_state: subscription.state as SubscriptionState,
					next_billing_date: subscription.expires_at,
					payment_method_status: subscription.state === 'pending_initial_payment' ? 'pending' : 'active',
				}
			}
		}

		if (!resolvedBilling) {
			return {
				isExpired: false,
				isInGracePeriod: false,
				gracePeriodDays: 0,
				status: 'active',
				nextBillingDate: null,
				expirationDate: null,
				isAccessBlocked: false,
			}
		}

		const subscriptionStatus = resolvedBilling.subscription_status
		const nextBillingDate = resolvedBilling.next_billing_date || null

		// If status is expired or payment_failed, check grace period
		if (subscriptionStatus === 'expired' || subscriptionStatus === 'payment_failed') {
			if (!nextBillingDate) {
				return {
					isExpired: true,
					isInGracePeriod: false,
					gracePeriodDays: 0,
					status: 'expired',
					nextBillingDate: null,
					expirationDate: nextBillingDate,
					isAccessBlocked: true,
				}
			}

			const graceStatus = getGracePeriodStatus(nextBillingDate)
			const { daysRemaining } = isInGracePeriod(nextBillingDate)

			const status =
				graceStatus === 'full_access'
					? 'grace_full_access'
					: graceStatus === 'restricted'
						? 'grace_restricted'
						: 'expired'

			return {
				isExpired: true,
				isInGracePeriod: graceStatus !== 'expired',
				gracePeriodDays: Math.max(0, daysRemaining),
				status,
				nextBillingDate,
				expirationDate: nextBillingDate,
				isAccessBlocked: status === 'expired' || status === 'grace_restricted',
			}
		}

		// Active or trial status
		return {
			isExpired: false,
			isInGracePeriod: false,
			gracePeriodDays: 0,
			status: 'active',
			nextBillingDate,
			expirationDate: null,
			isAccessBlocked: false,
		}
	}, [billingStatus, user, currentOrgId, currentTenant])
}
